import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight, Globe, Lock, Plus, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { Section } from "@/app/Page";
import { useEnvName, useProjectSlug } from "@/app/context";
import { Button, Card, EmptyState, ErrorState, ExplainPanel, RelativeTime, ShowAsCode, Skeleton, StatusBadge, describeStatus } from "@/design";
import { useDomains, useServiceInstances, type Domain } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/$workspace/$project/settings/domains")({
  staticData: { title: "Domains" },
  component: DomainsSettings,
});

const ORDER: Record<string, number> = { orphaned: 0, pending: 1, verified: 2, cert_issued: 3, active: 4 };

function DomainsSettings() {
  const project = useProjectSlug();
  const env = useEnvName();
  const q = useDomains(project, env);
  const instances = useServiceInstances(project, env);
  const nameOf = (id: string) => instances.data?.data.find((i) => i.service_id === id)?.name ?? "a service";

  const { custom, generated } = useMemo(() => {
    const all = q.data?.data ?? [];
    return {
      custom: all.filter((d) => d.kind === "custom").sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.hostname.localeCompare(b.hostname)),
      generated: all.filter((d) => d.kind === "generated"),
    };
  }, [q.data]);

  const addButton = (
    <Button asChild variant="primary" icon={<Plus />}>
      <Link to="/$workspace/$project/domains/new" params={(p) => p as never} search={(s) => ({ env: (s as { env?: string }).env }) as never}>
        Add a domain
      </Link>
    </Button>
  );

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  const attention = custom.filter((d) => d.status === "orphaned").length;
  const waiting = custom.filter((d) => d.status !== "active" && d.status !== "orphaned").length;

  return (
    <>
      <Section
        title="Your domains"
        description={
          q.isPending
            ? "Loading…"
            : custom.length === 0
              ? `No custom domains in ${env} yet.`
              : [
                  `${custom.length - waiting - attention} live`,
                  waiting ? `${waiting} being set up` : null,
                  attention ? `${attention} need${attention === 1 ? "s" : ""} attention` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
        }
        actions={
          <>
            <span className="hidden sm:inline-flex">
              <ShowAsCode title="List domains" cli={`guv domains list --env ${env}\nguv domains add www.example.co.uk --service web --env ${env}`} apiCall={{ method: "GET", path: `/v1/projects/${project}/environments/${env}/domains` }} />
            </span>
            {custom.length > 0 && addButton}
          </>
        }
      >
        {q.isPending ? (
          <Card>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-border px-5 py-4 last:border-0">
                <Skeleton className="size-9 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-3.5 w-80 max-w-full" />
                </div>
              </div>
            ))}
          </Card>
        ) : custom.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Globe />}
              title="Use your own domain"
              description="Point an address you own, like www.yourbusiness.co.uk, at your app. We walk you through your registrar's screens and set up HTTPS for you."
              actions={addButton}
            />
          </Card>
        ) : (
          <Card>
            <ul className="divide-y divide-border">
              {custom.map((d) => (
                <DomainRow key={d.id} d={d} service={nameOf(d.service_id)} />
              ))}
            </ul>
          </Card>
        )}
      </Section>

      <Section
        title="Guv addresses"
        description="Every web service gets a free address with HTTPS. It keeps working alongside your own domains."
        actions={
          <ExplainPanel title="Why keep the Guv address?" side="left">
            <p>It is handy for testing and for health checks, and it never depends on your DNS. Search engines are told your own domain is the main one.</p>
          </ExplainPanel>
        }
      >
        {q.isPending ? (
          <Skeleton className="h-16 w-full rounded-xl" />
        ) : generated.length === 0 ? (
          <Card>
            <EmptyState size="sm" icon={<Globe />} title="No web services yet" description="Deploy a web service and its free address appears here." />
          </Card>
        ) : (
          <Card>
            <ul className="divide-y divide-border">
              {generated.map((d) => (
                <li key={d.id}>
                  <a href={`https://${d.hostname}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-5 py-3 hover:bg-surface-2/60">
                    <Lock className="size-4 shrink-0 text-ok-text" aria-label="HTTPS" />
                    <span className="min-w-0 flex-1 truncate font-mono text-sm text-text">{d.hostname}</span>
                    <span className="hidden text-sm text-muted sm:inline">{nameOf(d.service_id)}</span>
                    <ArrowRight className="size-4 text-subtle" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </Section>
    </>
  );
}

function DomainRow({ d, service }: { d: Domain; service: string }) {
  const info = describeStatus("domain", d.status);
  const orphan = d.status === "orphaned";
  const setup = d.status !== "active" && !orphan;
  return (
    <li>
      <Link
        to="/$workspace/$project/domains/$domain"
        params={(p) => ({ ...(p as Record<string, string>), domain: d.hostname }) as never}
        search={(s) => ({ env: (s as { env?: string }).env }) as never}
        className={cn("group flex items-start gap-4 px-4 py-4 transition-colors hover:bg-surface-2/60 sm:px-5", orphan && "bg-danger-soft/40")}
      >
        <span
          className={cn(
            "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg [&_svg]:size-4",
            orphan ? "bg-danger-soft text-danger-text" : setup ? "bg-warn-soft text-warn-text" : "bg-ok-soft text-ok-text",
          )}
          aria-hidden="true"
        >
          {orphan ? <TriangleAlert /> : d.status === "active" ? <Lock /> : <Globe />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="break-all font-medium text-text group-hover:text-accent-text">{d.hostname}</span>
            <StatusBadge kind="domain" status={d.status} variant="pill" />
          </span>
          <span className={cn("mt-1 block text-sm", orphan ? "text-danger-text" : "text-muted")}>
            {orphan ? (d.error?.message ?? info.explain) : (d.status_text ?? info.explain)}
          </span>
          <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-subtle">
            <span>→ {service}</span>
            {d.registrar?.name && <span>DNS at {d.registrar.name}</span>}
            {d.status === "active" && d.certificate?.expires_at && <span>HTTPS renews before {formatDate(d.certificate.expires_at)}</span>}
            {orphan && d.release_at && <span className="font-medium text-danger-text">Released on {formatDate(d.release_at)} unless fixed</span>}
            {setup && d.last_checked_at && !d.status_text?.includes("checked") && (
              <span>
                Checked <RelativeTime value={d.last_checked_at} />
              </span>
            )}
          </span>
        </span>
        <span className="mt-1 hidden shrink-0 items-center gap-1 text-sm font-medium text-accent-text sm:inline-flex">
          {orphan ? "Fix it" : setup ? "Continue setup" : "Details"}
          <ChevronRight className="size-4" />
        </span>
        <ChevronRight className="mt-2 size-4 shrink-0 text-subtle sm:hidden" aria-hidden="true" />
      </Link>
    </li>
  );
}
